import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  Zoom,
  FormHelperText,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Link as MuiLink
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import InternationalPhoneField from '../../components/common/InternationalPhoneField';
import { useAppBranding } from '../../context/AppBrandingContext';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Login as LoginIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Category as DepartmentIcon,
  Schedule as TimezoneIcon,
  PhotoCamera as CameraIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Google as GoogleIcon,
  LinkedIn as LinkedInIcon,
  Microsoft as MicrosoftIcon,
  MarkEmailRead as EmailSentIcon
} from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import api from '../../utils/api';
import { SIGNUP_AS_OPTIONS } from '../contacts/contactRolesUtil';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';
import SharedTemplateChooser from '../../components/public/SharedTemplateChooser';
import PublicSiteNav from '../../components/public/PublicSiteNav';
import AuthorizedAccessDisclaimer from '../../components/public/AuthorizedAccessDisclaimer';
import { LOGIN_PORTAL_THEMES as THEMES } from '../../theme/loginPortalThemes';
import {
  getSharedPageAtmosphere,
  subscribeSharedPageAtmosphere
} from '../../utils/sharedPageAtmosphere';

// Departments list
const DEPARTMENTS = [
  'Engineering', 'Sales', 'Marketing', 'Human Resources', 'Finance',
  'Operations', 'Customer Support', 'Product Management', 'Design',
  'Legal', 'IT', 'Other'
];

// Common timezones
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European (CET)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' }
];

// Password strength calculator
const calculatePasswordStrength = (password) => {
  let strength = 0;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    longLength: password.length >= 12
  };

  if (checks.length) strength += 20;
  if (checks.uppercase) strength += 20;
  if (checks.lowercase) strength += 15;
  if (checks.number) strength += 20;
  if (checks.special) strength += 15;
  if (checks.longLength) strength += 10;

  return { strength: Math.min(strength, 100), checks };
};

const getStrengthColor = (strength) => {
  if (strength < 30) return '#f44336';
  if (strength < 50) return '#ff9800';
  if (strength < 70) return '#ffeb3b';
  if (strength < 90) return '#8bc34a';
  return '#4caf50';
};

const getStrengthLabel = (strength) => {
  if (strength < 30) return 'Weak';
  if (strength < 50) return 'Fair';
  if (strength < 70) return 'Good';
  if (strength < 90) return 'Strong';
  return 'Excellent';
};

// Steps for the wizard
const steps = ['Personal Info', 'Professional Info', 'Security'];

const Signup = () => {
  const navigate = useNavigate();
  const { footerText, companyName, displayCompanyName } = useAppBranding();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    job_title: '',
    department: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    signup_as: 'contact',
    password: '',
    confirmPassword: '',
    avatar: null,
    avatarPreview: null,
    agreeTerms: false,
    agreePrivacy: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [theme, setTheme] = useState(() => getSharedPageAtmosphere());
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaDisplay, setCaptchaDisplay] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [humanVerified, setHumanVerified] = useState(false);
  const [blockedDomains, setBlockedDomains] = useState([]);
  const [signupBlockedDialogOpen, setSignupBlockedDialogOpen] = useState(false);
  const [signupConfig, setSignupConfig] = useState({
    loading: true,
    enabled: true,
    providers: []
  });

  const currentTheme = THEMES[theme] || THEMES.light;
  const passwordStrength = calculatePasswordStrength(formData.password);
  const navBarVariant = ['light', 'aqua', 'orchid'].includes(theme) ? 'light' : 'dark';

  useEffect(() => subscribeSharedPageAtmosphere(setTheme), []);

  useEffect(() => {
    const loadSignupConfig = async () => {
      try {
        const response = await api.get('/public/signup-config');
        const data = response.data?.data || {};
        const allowed = Array.isArray(data.providers)
          ? data.providers
              .map((v) => String(v || '').toLowerCase().trim())
              .filter((v) => ['google', 'linkedin', 'microsoft'].includes(v))
          : [];
        setSignupConfig({
          loading: false,
          enabled: data.enabled !== false,
          providers: allowed
        });
      } catch {
        setSignupConfig({ loading: false, enabled: true, providers: [] });
      }
    };
    loadSignupConfig();
  }, []);

  // Fetch blocked domains on mount
  const loadSignupCaptcha = useCallback(async () => {
    try {
      const response = await api.get('/auth/signup-captcha');
      const d = response.data?.data;
      if (d?.captchaToken && d?.captchaDisplay) {
        setCaptchaToken(d.captchaToken);
        setCaptchaDisplay(d.captchaDisplay);
        setCaptchaInput('');
      }
    } catch {
      setCaptchaToken('');
      setCaptchaDisplay('');
    }
  }, []);

  useEffect(() => {
    const fetchBlockedDomains = async () => {
      try {
        const response = await api.get('/auth/blocked-domains');
        setBlockedDomains(response.data.data || []);
      } catch {
        // Silently fail - domains will be checked on backend
      }
    };
    fetchBlockedDomains();
  }, []);

  useEffect(() => {
    if (signupConfig.enabled) loadSignupCaptcha();
  }, [signupConfig.enabled, loadSignupCaptcha]);

  // Email availability check (debounced)
  const checkEmailAvailability = useCallback(async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailAvailable(null);
      setEmailError('');
      return;
    }

    // Check blocked domains on frontend
    const domain = email.split('@')[1]?.toLowerCase();
    if (blockedDomains.includes(domain)) {
      setEmailAvailable(false);
      setEmailError(`Email domain @${domain} is not allowed for registration`);
      return;
    }
    
    setEmailChecking(true);
    setEmailError('');
    try {
      const response = await api.post('/auth/check-email', { email });
      if (response.data.data.blocked) {
        setEmailAvailable(false);
        setEmailError(response.data.data.message || 'This email domain is not allowed');
      } else {
        setEmailAvailable(response.data.data.available);
        if (!response.data.data.available) {
          setEmailError('This email is already registered');
        }
      }
    } catch {
      setEmailAvailable(null);
    } finally {
      setEmailChecking(false);
    }
  }, [blockedDomains]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.email) {
        checkEmailAvailability(formData.email);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.email, checkEmailAvailability]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Avatar image must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          avatar: file,
          avatarPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCaptchaInputChange = (e) => {
    setCaptchaInput(e.target.value);
    if (validationErrors.captcha) {
      setValidationErrors((prev) => ({ ...prev, captcha: null }));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    
    if (step === 0) {
      if (!formData.first_name.trim()) errors.first_name = 'First name is required';
      if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
      if (!formData.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      } else if (emailAvailable === false) {
        errors.email = emailError || 'This email is not available';
      }
    }
    
    if (step === 2) {
      if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      } else if (passwordStrength.strength < 50) {
        errors.password = 'Please choose a stronger password';
      }
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.agreeTerms) {
        errors.agreeTerms = 'You must agree to the Terms of Service';
      }
      if (!formData.agreePrivacy) {
        errors.agreePrivacy = 'You must agree to the Privacy Policy';
      }
      if (!humanVerified) {
        errors.captcha = 'Confirm you are not a robot, then enter the verification code';
      } else if (!captchaInput || String(captchaInput).trim().length < 4) {
        errors.captcha = 'Enter the verification code shown above';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!signupConfig.enabled) {
      setError('Signup is currently disabled by administrator.');
      return;
    }
    if (!validateStep(2)) return;
    
    setLoading(true);
    setError('');

    try {
      await api.post('/auth/signup', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone || null,
        company: formData.company || null,
        job_title: formData.job_title || null,
        department: formData.department || null,
        timezone: formData.timezone,
        signup_as: formData.signup_as || 'contact',
        password: formData.password,
        captchaToken,
        captchaInput: String(captchaInput).trim()
      });
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Signup failed. Please try again.');
      loadSignupCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    alert(`${provider} login coming soon! Please use email registration for now.`);
  };

  const showGoogleSignup = signupConfig.providers.includes('google');
  const showLinkedinSignup = signupConfig.providers.includes('linkedin');
  const showMicrosoftSignup = signupConfig.providers.includes('microsoft');
  const socialSignupButtonCount =
    (showGoogleSignup ? 1 : 0) + (showLinkedinSignup ? 1 : 0) + (showMicrosoftSignup ? 1 : 0);

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: currentTheme.inputBg,
      '& fieldset': {
        borderColor: theme === 'light' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.15)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(99, 102, 241, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#6366f1',
      },
    },
    '& .MuiInputLabel-root': {
      color: currentTheme.textSecondary,
    },
    '& .MuiOutlinedInput-input': {
      color: currentTheme.textPrimary,
    },
    '& .MuiSelect-icon': {
      color: currentTheme.textSecondary,
    }
  };

  if (signupConfig.loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: currentTheme.background }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!signupConfig.enabled) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: currentTheme.background,
          px: 2
        }}
      >
        <Card sx={{ maxWidth: 560, width: '100%', backgroundColor: currentTheme.cardBg, border: `1px solid ${currentTheme.cardBorder}` }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: currentTheme.textPrimary }}>
              Signup is currently disabled
            </Typography>
            <Typography variant="body2" sx={{ color: currentTheme.textSecondary, mb: 3 }}>
              Public registrations are turned off by the administrator right now. Please contact support or return later.
            </Typography>
            <Button component={Link} to="/" variant="contained" startIcon={<HomeIcon />}>
              Back to home
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Success Screen - Email Activation Required
  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: currentTheme.background,
          px: 2,
          position: 'relative'
        }}
      >
        <Button
          component={Link}
          to="/"
          startIcon={<HomeIcon />}
          sx={{ position: 'absolute', top: 20, left: 20, zIndex: 2, color: currentTheme.textPrimary }}
        >
          Back to home
        </Button>
        <Zoom in={success}>
          <Card
            sx={{
              maxWidth: 550,
              width: '100%',
              backgroundColor: currentTheme.cardBg,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${currentTheme.cardBorder}`,
              textAlign: 'center',
              p: 4
            }}
          >
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2196f3 0%, #21cbf3 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                animation: 'pulse 2s infinite'
              }}
            >
              <EmailSentIcon sx={{ fontSize: 50, color: '#fff' }} />
            </Box>
            
            <Typography variant="h4" sx={{ color: currentTheme.textPrimary, mb: 2, fontWeight: 700 }}>
              Check Your Email! 📧
            </Typography>
            
            <Typography variant="body1" sx={{ color: currentTheme.textSecondary, mb: 3 }}>
              We've sent an activation link to:
              <br />
              <strong style={{ color: currentTheme.textPrimary }}>{formData.email}</strong>
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <strong>Registration Process:</strong>
              <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li><strong>Step 1:</strong> Click the activation link in your email</li>
                <li><strong>Step 2:</strong> Wait for administrator approval</li>
                <li><strong>Step 3:</strong> Once approved, you'll receive a confirmation email</li>
                <li><strong>Step 4:</strong> Login and start using the system</li>
              </ol>
            </Alert>

            <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
              <strong>Note:</strong> This is a managed system. All registrations require administrator approval. 
              You will not be able to login until an administrator reviews and approves your account.
            </Alert>

            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
              An administrator will review your request under <strong>Pending Approvals</strong>. You are not granted access
              until they approve. After approval, a contact record is created (Guest — signed up) so your profile can be
              promoted to a standard contact type when appropriate.
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
                startIcon={<LoginIcon />}
                sx={{
                  borderColor: currentTheme.cardBorder,
                  color: currentTheme.textPrimary
                }}
              >
                Back to Login
              </Button>
              <Button
                component={Link}
                to="/"
                variant="outlined"
                startIcon={<HomeIcon />}
                sx={{
                  borderColor: currentTheme.cardBorder,
                  color: currentTheme.textPrimary
                }}
              >
                Back to home
              </Button>
              <Button
                variant="contained"
                onClick={() => window.open('https://mail.google.com', '_blank')}
                startIcon={<EmailIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                }}
              >
                Open Email
              </Button>
            </Box>
          </Card>
        </Zoom>
        
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.05); }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </Box>
    );
  }

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            {/* Avatar Upload */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatar-upload"
                type="file"
                onChange={handleAvatarChange}
              />
              <label htmlFor="avatar-upload">
                <Box sx={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}>
                  <Avatar
                    src={formData.avatarPreview}
                    sx={{
                      width: 100,
                      height: 100,
                      border: `3px solid ${currentTheme.cardBorder}`,
                      backgroundColor: currentTheme.inputBg
                    }}
                  >
                    {!formData.avatarPreview && <PersonIcon sx={{ fontSize: 50 }} />}
                  </Avatar>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: '#6366f1',
                      borderRadius: '50%',
                      p: 0.5
                    }}
                  >
                    <CameraIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                </Box>
              </label>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: currentTheme.textSecondary }}>
                Click to upload avatar (optional)
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="first_name"
                  label="First Name *"
                  placeholder="Enter your first name"
                  value={formData.first_name}
                  onChange={handleChange}
                  error={!!validationErrors.first_name}
                  helperText={validationErrors.first_name}
                  sx={inputStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: currentTheme.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="last_name"
                  label="Last Name *"
                  placeholder="Enter your last name"
                  value={formData.last_name}
                  onChange={handleChange}
                  error={!!validationErrors.last_name}
                  helperText={validationErrors.last_name}
                  sx={inputStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: currentTheme.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
              <FormLabel
                component="legend"
                sx={{ color: currentTheme.textSecondary, mb: 1, fontSize: '0.875rem' }}
              >
                Sign up as
              </FormLabel>
              <RadioGroup
                name="signup_as"
                value={formData.signup_as || 'contact'}
                onChange={handleChange}
              >
                {SIGNUP_AS_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio sx={{ color: currentTheme.textSecondary, '&.Mui-checked': { color: '#6366f1' } }} />}
                    label={
                      <Typography variant="body2" sx={{ color: currentTheme.textPrimary }}>
                        {opt.label}
                        {opt.value === 'contact' ? ' — you can become a Candidate later' : ''}
                        {opt.value === 'candidate' ? ' — Contact + Candidate (Job Seeker)' : ''}
                        {opt.value === 'trainer' ? ' — Contact + Trainer / Instructor / Teacher' : ''}
                      </Typography>
                    }
                    sx={{ alignItems: 'flex-start', mb: 0.5 }}
                  />
                ))}
              </RadioGroup>
              <Typography variant="caption" sx={{ color: currentTheme.textSecondary, display: 'block', mt: 0.5 }}>
                Everyone starts as Contact. Progression: Contact → Candidate → Employee or Contractor (Independent / C2C).
              </Typography>
            </FormControl>

            <TextField
              fullWidth
              name="email"
              label="Email Address *"
              placeholder="Enter your email address"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!validationErrors.email || !!emailError}
              helperText={validationErrors.email || emailError}
              sx={{ ...inputStyles, mt: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: currentTheme.textSecondary }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {emailChecking && <CircularProgress size={20} />}
                    {!emailChecking && emailAvailable === true && (
                      <CheckIcon sx={{ color: '#4caf50' }} />
                    )}
                    {!emailChecking && emailAvailable === false && (
                      <CancelIcon sx={{ color: '#f44336' }} />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            {blockedDomains.length > 0 && (
              <MuiLink
                component="button"
                type="button"
                variant="body2"
                onClick={() => setSignupBlockedDialogOpen(true)}
                sx={{ mt: 1, display: 'block', textAlign: 'left', cursor: 'pointer' }}
              >
                See blocked domains for signup
              </MuiLink>
            )}

            <Dialog open={signupBlockedDialogOpen} onClose={() => setSignupBlockedDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Domains blocked for signup</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Registration is not allowed using an email address at these domains:
                </Typography>
                <List dense>
                  {blockedDomains.map((d) => (
                    <ListItem key={d} disablePadding>
                      <ListItemText primary={`@${d}`} primaryTypographyProps={{ fontFamily: 'monospace' }} />
                    </ListItem>
                  ))}
                </List>
              </DialogContent>
              <DialogActions>
                <Button variant="contained" onClick={() => setSignupBlockedDialogOpen(false)}>
                  OK
                </Button>
              </DialogActions>
            </Dialog>

            <Box sx={{ mt: 2, ...inputStyles }}>
              <InternationalPhoneField
                label="Phone number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Box>

            {/* Social Login Options */}
            {socialSignupButtonCount > 0 && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }}>
                  <Chip label="Or sign up with" size="small" sx={{ color: currentTheme.textSecondary }} />
                </Divider>
                <Grid container spacing={2}>
                  {showGoogleSignup && (
                    <Grid item xs={12 / socialSignupButtonCount}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleSocialLogin('Google')}
                        sx={{
                          borderColor: currentTheme.cardBorder,
                          color: currentTheme.textPrimary,
                          '&:hover': { borderColor: '#ea4335', bgcolor: 'rgba(234, 67, 53, 0.1)' }
                        }}
                      >
                        <GoogleIcon sx={{ color: '#ea4335' }} />
                      </Button>
                    </Grid>
                  )}
                  {showLinkedinSignup && (
                    <Grid item xs={12 / socialSignupButtonCount}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleSocialLogin('LinkedIn')}
                        sx={{
                          borderColor: currentTheme.cardBorder,
                          color: currentTheme.textPrimary,
                          '&:hover': { borderColor: '#0077b5', bgcolor: 'rgba(0, 119, 181, 0.1)' }
                        }}
                      >
                        <LinkedInIcon sx={{ color: '#0077b5' }} />
                      </Button>
                    </Grid>
                  )}
                  {showMicrosoftSignup && (
                    <Grid item xs={12 / socialSignupButtonCount}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleSocialLogin('Microsoft')}
                        sx={{
                          borderColor: currentTheme.cardBorder,
                          color: currentTheme.textPrimary,
                          '&:hover': { borderColor: '#00a4ef', bgcolor: 'rgba(0, 164, 239, 0.1)' }
                        }}
                      >
                        <MicrosoftIcon sx={{ color: '#00a4ef' }} />
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <TextField
              fullWidth
              name="company"
              label="Company / Organization"
              placeholder="Enter your company name"
              value={formData.company}
              onChange={handleChange}
              sx={inputStyles}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: currentTheme.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="job_title"
              label="Job Title / Role"
              placeholder="Enter your job title"
              value={formData.job_title}
              onChange={handleChange}
              sx={{ ...inputStyles, mt: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WorkIcon sx={{ color: currentTheme.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth sx={{ ...inputStyles, mt: 2 }}>
              <InputLabel sx={{ color: currentTheme.textSecondary }}>Department</InputLabel>
              <Select
                name="department"
                value={formData.department}
                onChange={handleChange}
                label="Department"
                startAdornment={
                  <InputAdornment position="start">
                    <DepartmentIcon sx={{ color: currentTheme.textSecondary }} />
                  </InputAdornment>
                }
              >
                <MenuItem value=""><em>Select Department</em></MenuItem>
                {DEPARTMENTS.map(dept => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ ...inputStyles, mt: 2 }}>
              <InputLabel sx={{ color: currentTheme.textSecondary }}>Timezone</InputLabel>
              <Select
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                label="Timezone"
                startAdornment={
                  <InputAdornment position="start">
                    <TimezoneIcon sx={{ color: currentTheme.textSecondary }} />
                  </InputAdornment>
                }
              >
                {TIMEZONES.map(tz => (
                  <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 3 }}>
              Professional information helps us personalize your experience. All fields are optional.
            </Alert>
          </Box>
        );

      case 2:
        return (
          <Box>
            <TextField
              fullWidth
              name="password"
              label="Password *"
              placeholder="Create a strong password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              error={!!validationErrors.password}
              helperText={validationErrors.password}
              sx={inputStyles}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: currentTheme.textSecondary }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: currentTheme.textSecondary }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Strength Indicator */}
            {formData.password && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: currentTheme.textSecondary }}>
                    Password Strength
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ color: getStrengthColor(passwordStrength.strength), fontWeight: 600 }}
                  >
                    {getStrengthLabel(passwordStrength.strength)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength.strength}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: getStrengthColor(passwordStrength.strength),
                      borderRadius: 3
                    }
                  }}
                />
                
                <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    { check: passwordStrength.checks.length, label: '8+ chars' },
                    { check: passwordStrength.checks.uppercase, label: 'Uppercase' },
                    { check: passwordStrength.checks.lowercase, label: 'Lowercase' },
                    { check: passwordStrength.checks.number, label: 'Number' },
                    { check: passwordStrength.checks.special, label: 'Special' }
                  ].map((req, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={req.label}
                      icon={req.check ? <CheckIcon /> : <CancelIcon />}
                      sx={{
                        bgcolor: req.check ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                        color: req.check ? '#4caf50' : '#f44336',
                        '& .MuiChip-icon': { color: 'inherit' }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <TextField
              fullWidth
              name="confirmPassword"
              label="Confirm Password *"
              placeholder="Confirm your password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!validationErrors.confirmPassword}
              helperText={validationErrors.confirmPassword}
              sx={{ ...inputStyles, mt: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: currentTheme.textSecondary }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: currentTheme.textSecondary }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    {formData.confirmPassword && formData.password === formData.confirmPassword && (
                      <CheckIcon sx={{ color: '#4caf50', ml: 1 }} />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: currentTheme.cardBorder,
                bgcolor: currentTheme.inputBg,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: currentTheme.textPrimary, fontWeight: 700, mb: 0.5 }}>
                reCAPTCHA — robot check
              </Typography>
              <Typography variant="caption" sx={{ color: currentTheme.textSecondary, display: 'block', mb: 1.5 }}>
                Confirm you are human before we accept this registration. Codes expire in a few minutes.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={humanVerified}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setHumanVerified(on);
                      if (on) loadSignupCaptcha();
                      else {
                        setCaptchaInput('');
                        setValidationErrors((prev) => ({ ...prev, captcha: undefined }));
                      }
                    }}
                    sx={{ color: currentTheme.textSecondary }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: currentTheme.textPrimary, fontWeight: 600 }}>
                    I&apos;m not a robot
                  </Typography>
                }
              />
              {humanVerified && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        px: 2,
                        py: 1.5,
                        flex: 1,
                        minWidth: 200,
                        textAlign: 'center',
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 22,
                        letterSpacing: 6,
                        userSelect: 'none',
                        bgcolor: alpha('#000', 0.2),
                        borderColor: currentTheme.cardBorder,
                        color: currentTheme.textPrimary,
                      }}
                    >
                      {captchaDisplay || '······'}
                    </Paper>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={loadSignupCaptcha}
                      sx={{ color: currentTheme.textPrimary, borderColor: currentTheme.cardBorder }}
                    >
                      New code
                    </Button>
                  </Box>
                  <TextField
                    fullWidth
                    name="captchaInput"
                    label="Enter the verification code"
                    value={captchaInput}
                    onChange={handleCaptchaInputChange}
                    error={!!validationErrors.captcha}
                    helperText={validationErrors.captcha || 'Type the characters shown above'}
                    autoComplete="off"
                    inputProps={{ autoComplete: 'off' }}
                    sx={inputStyles}
                  />
                </Box>
              )}
              {!humanVerified && validationErrors.captcha && (
                <FormHelperText error sx={{ mt: 1 }}>{validationErrors.captcha}</FormHelperText>
              )}
            </Box>

            {/* Terms & Conditions */}
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    sx={{ color: currentTheme.textSecondary }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: currentTheme.textSecondary }}>
                    I agree to the{' '}
                    <Link to="/terms" style={{ color: '#8b5cf6' }}>Terms of Service</Link> *
                  </Typography>
                }
              />
              {validationErrors.agreeTerms && (
                <FormHelperText error>{validationErrors.agreeTerms}</FormHelperText>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    name="agreePrivacy"
                    checked={formData.agreePrivacy}
                    onChange={handleChange}
                    sx={{ color: currentTheme.textSecondary }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: currentTheme.textSecondary }}>
                    I agree to the{' '}
                    <Link to="/privacy" style={{ color: '#8b5cf6' }}>Privacy Policy</Link> *
                  </Typography>
                }
              />
              {validationErrors.agreePrivacy && (
                <FormHelperText error>{validationErrors.agreePrivacy}</FormHelperText>
              )}
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: currentTheme.background,
        position: 'relative',
        overflow: 'hidden',
        transition: 'background 0.4s ease-in-out'
      }}
    >
      <PublicSiteNav barVariant={navBarVariant} active="register" />
      <SharedTemplateChooser
        title="Template chooser"
        subtitle="Same look across Sign In, Register, Browsing, and Landing."
        topOffset={{ xs: 68, sm: 76 }}
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          minHeight: 0
        }}
      >
      {/* Animated Background Particles */}
      <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {[...Array(20)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: Math.random() * 10 + 5,
              height: Math.random() * 10 + 5,
              borderRadius: '50%',
              background: `rgba(99, 102, 241, ${Math.random() * 0.3 + 0.1})`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 10 + 10}s linear infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </Box>

      {/* Background Decorations */}
      <Box
        sx={{
          position: 'absolute', width: 400, height: 400, borderRadius: '50%',
          background: `radial-gradient(circle, ${currentTheme.decorColor1 || 'rgba(99, 102, 241, 0.15)'} 0%, transparent 70%)`,
          top: -100, right: -100,
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute', width: 300, height: 300, borderRadius: '50%',
          background: `radial-gradient(circle, ${currentTheme.decorColor2 || 'rgba(236, 72, 153, 0.1)'} 0%, transparent 70%)`,
          bottom: -50, left: -50,
          pointerEvents: 'none'
        }}
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 3, sm: 4 },
          position: 'relative',
          zIndex: 1
        }}
      >
      <Card
        sx={{
          maxWidth: 600,
          width: '100%',
          backgroundColor: currentTheme.cardBg,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${currentTheme.cardBorder}`,
          boxShadow: theme === 'light' 
            ? '0 8px 32px rgba(99, 102, 241, 0.15)' 
            : '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <strong>Signing up does not give access to the app.</strong> An administrator must approve you under{' '}
            <strong>Pending Approvals</strong> first. You will get email instructions after you verify your address.
          </Alert>
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              ERP Consulting
            </Typography>
            <Typography variant="body2" sx={{ color: currentTheme.textSecondary }}>
              Create your account
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': { color: currentTheme.textSecondary },
                    '& .MuiStepLabel-label.Mui-active': { color: currentTheme.textPrimary },
                    '& .MuiStepLabel-label.Mui-completed': { color: '#4caf50' }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {renderStepContent()}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                type="button"
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<BackIcon />}
                sx={{ 
                  color: currentTheme.textSecondary,
                  visibility: activeStep === 0 ? 'hidden' : 'visible'
                }}
              >
                Back
              </Button>
              
              {activeStep < steps.length - 1 ? (
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<NextIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    }
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #388e3c 0%, #689f38 100%)',
                    },
                    minWidth: 150
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
                </Button>
              )}
            </Box>
          </form>

          {/* Login Link */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: currentTheme.textSecondary }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: '#8b5cf6', textDecoration: 'none', fontWeight: 600 }}>
                Sign In
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
      </Box>

      {/* Legal notice below the centered signup card */}
      <Box
        sx={{
          flexShrink: 0,
          width: '100%',
          maxWidth: 720,
          mx: 'auto',
          px: 2,
          pb: 3,
          pt: 0,
          position: 'relative',
          zIndex: 1
        }}
      >
        <AuthorizedAccessDisclaimer
          companyName={displayCompanyName || companyName}
          textPrimary={currentTheme.textPrimary}
          textSecondary={currentTheme.textSecondary}
          sx={{ mb: 1.5 }}
        />
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', color: currentTheme.textSecondary, opacity: 0.7 }}
        >
          {footerText}
        </Typography>
      </Box>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.5; }
            50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
          }
        `}
      </style>
      </Box>
    </Box>
  );
};

export default Signup;
